import { z } from 'zod';
import { decimalNoNegativo, rif, uuid } from './comun.ts';
import { FORMATOS_DESPACHO, plantillaFacturaInput, type FormatoDespacho, type PlantillaFactura } from './impresion.ts';

export const configActualizarInput = z.object({ valor: z.union([z.string().trim().max(500), z.number(), z.boolean()]) });
export type ConfigActualizarInput = z.infer<typeof configActualizarInput>;

/**
 * Teléfono o WhatsApp de una tienda: salen impresos en el membrete de todos los formatos, así que solo números con
 * espacios, guiones, puntos, paréntesis, «+» o «/» (revisión de seguridad del pedido de la dueña, 2026-10-19). Vacío = sin
 * número.
 */
const telefonoTienda = z.preprocess(
  (v) => (typeof v === 'string' && v.trim() === '' ? null : v),
  z.string().trim().max(40).regex(/^[0-9+().\-\s/]{7,40}$/, 'Escribe solo el número (dígitos, espacios, guiones, puntos o paréntesis)').nullable().optional(),
);

export const sucursalInput = z.object({
  codigo: z.string().trim().min(2).max(10).toUpperCase(),
  nombre: z.string().trim().min(2).max(80),
  /** Letra de la serie fiscal; vacía = forma libre sin letra (Barquisimeto). */
  serie: z.string().trim().max(1).toUpperCase().regex(/^[A-Z]?$/, 'La serie es una letra A-Z o vacía (sin letra)'),
  /** Serie del talonario manual de la sucursal (A en Barquisimeto, B en Barinas). */
  serie_manual: z.string().trim().toUpperCase().regex(/^[A-Z]$/, 'Una letra A-Z').nullable().optional(),
  direccion_fiscal: z.string().trim().min(5).max(300),
  telefono: telefonoTienda,
  /** Celular de WhatsApp de la tienda: sale en el membrete junto al fijo (pedido de la dueña 2026-10-19). */
  whatsapp: telefonoTienda,
  formato_impresion: z.enum(['carta', 'media_carta', 'media_carta_vertical']).default('carta'),
  /** Plantilla del papel de factura: lo que se indique pisa las medidas de partida del papel de la sucursal. */
  plantilla_factura: plantillaFacturaInput.partial().optional(),
});
export type SucursalInput = z.infer<typeof sucursalInput>;
/**
 * Edición PARCIAL: sin los `.default()` del alta. En zod 4 un default sobrevive a `.partial()` y pisaría lo que no se
 * envió (cambiar el teléfono de Barinas la devolvía a «carta»; bug reproducido en test/bugs/edicion-parcial).
 */
export const sucursalEditarInput = sucursalInput.partial().extend({ formato_impresion: z.enum(['carta', 'media_carta', 'media_carta_vertical']).optional(), activa: z.boolean().optional() });

export const almacenInput = z.object({
  sucursal_id: uuid,
  codigo: z.string().trim().min(2).max(20).toUpperCase(),
  nombre: z.string().trim().min(2).max(80),
  tipo: z.enum(['tienda', 'deposito']).default('deposito'),
  principal: z.boolean().default(false),
  /** Dirección propia (depósitos): va en la guía de traslado. Vacía = la dirección fiscal de su tienda. */
  direccion: z.string().trim().max(200).nullable().optional(),
});
export type AlmacenInput = z.infer<typeof almacenInput>;
/** Edición PARCIAL: sin los `.default()` del alta (editar la dirección del almacén principal le quitaba lo de principal). */
export const almacenEditarInput = almacenInput.omit({ sucursal_id: true }).partial().extend({ tipo: z.enum(['tienda', 'deposito']).optional(), principal: z.boolean().optional(), activo: z.boolean().optional() });

export const MODALIDADES_CAJA = ['completa', 'solo_fiscal'] as const;
const cajaBase = z.object({
  codigo: z.string().trim().min(1).max(10).toUpperCase(),
  nombre: z.string().trim().min(2).max(60),
  /** Fondo fijo que queda en la caja de un turno al siguiente. */
  fondo_usd: decimalNoNegativo,
  fondo_bs: decimalNoNegativo,
  /** Banco del punto de venta de esta caja (se anota en cada cobro por punto). */
  punto_banco: z.string().trim().max(40).nullable(),
  /** completa = facturas y notas · solo_fiscal = solo facturas. */
  modalidad: z.enum(MODALIDADES_CAJA),
  /** Formato del despacho que imprime esta caja: media carta vertical o tiquera de 80 mm. */
  formato_despacho: z.enum(FORMATOS_DESPACHO),
});
/** Alta: fondo 0, sin banco y modalidad completa si no se indican. */
export const cajaInput = cajaBase.partial({ fondo_usd: true, fondo_bs: true, punto_banco: true, modalidad: true, formato_despacho: true }).extend({ sucursal_id: uuid });
export type CajaInput = z.infer<typeof cajaInput>;
export const cajaEditarInput = cajaBase.partial().extend({ activa: z.boolean().optional() });

/** Wizard de arranque (instalación nueva sin sucursales). */
export const arranqueInput = z.object({
  sucursal: sucursalInput,
  almacen_nombre: z.string().trim().min(2).max(80).default('Tienda principal'),
  caja_nombre: z.string().trim().min(2).max(60).default('Caja 1'),
});
export type ArranqueInput = z.infer<typeof arranqueInput>;

export interface ConfigEntradaDTO {
  clave: string;
  valor: string | number | boolean;
  descripcion: string;
  tipo: 'texto' | 'numero' | 'booleano';
  actualizado_en: string;
  actualizado_por_nombre: string | null;
}

export interface EstructuraDTO {
  sucursales: { id: string; codigo: string; nombre: string; serie: string; serie_manual: string | null; direccion_fiscal: string; telefono: string | null; whatsapp: string | null; formato_impresion: 'carta' | 'media_carta' | 'media_carta_vertical'; plantilla_factura: PlantillaFactura; activa: boolean; almacenes: { id: string; codigo: string; nombre: string; tipo: 'tienda' | 'deposito'; principal: boolean; activo: boolean; direccion: string | null }[]; cajas: { id: string; codigo: string; nombre: string; activa: boolean; fondo_usd: string; fondo_bs: string; punto_banco: string | null; modalidad: 'completa' | 'solo_fiscal'; formato_despacho: FormatoDespacho }[] }[];
}

export interface DashboardSucursalDTO {
  sucursal_id: string;
  codigo: string;
  nombre: string;
  activa: boolean;
  documentos_hoy: number;
  ventas_hoy: string;
  ventas_semana: string;
  cajas_abiertas: { sesion_id: string; caja_nombre: string; usuario_nombre: string; abierta_en: string; cobrado_usd: string }[];
  alertas: { bajo_minimo: number; retenciones_30: number; diferencias_arqueo_hoy: string; ajustes_pendientes: number; gastos_pendientes: number; traslados_en_transito: number; /** Guías cerradas con faltante que nadie ha revisado (origen en esta tienda). */ diferencias_traslado: number; /** Ventas con material sin retirar pasado el plazo en días hábiles. */ sin_retirar: number };
  pendientes_entrega: number;
  cxc: string;
  cxp: string;
}

export interface DashboardDTO {
  generado_en: string;
  hoy: string;
  semana_desde: string;
  tasa: { bcv: string; paralela: string; factor_divisa: string; vigente_desde: string; horas: number; vieja: boolean };
  sucursales: DashboardSucursalDTO[];
  totales: { ventas_hoy: string; ventas_semana: string; documentos_hoy: number; cajas_abiertas: number; cxc: string; cxp: string; pendientes_entrega: number; alertas: number };
  /** `ventas_sin_costo`: la parte de las ventas del producto que se vendió sin costo cargado (su margen sale de más). */
  top_productos: { producto_id: string; codigo: string; nombre: string; unidades: string; ventas: string; costo: string; margen: string; margen_pct: string; ventas_sin_costo: string }[];
  requiere_wizard: boolean;
}

// ─── Correlativos de arranque y talonario de control (bloque 2, 2026-10-09) ──
/** Secuencias que la dueña fija al arrancar para continuar la numeración de su sistema anterior. */
export const TIPOS_ARRANQUE = ['FAC', 'NOTA', 'NC', 'ND', 'CTRL'] as const;
export type TipoArranque = (typeof TIPOS_ARRANQUE)[number];
export const TIPO_ARRANQUE_ETIQUETA: Record<TipoArranque, string> = {
  FAC: 'Factura',
  NOTA: 'Nota de entrega',
  NC: 'Nota de crédito',
  ND: 'Nota de débito',
  CTRL: 'Número de control (papel fiscal)',
};

/** `ultimo` = último número YA usado (en el sistema anterior o en el talonario); el próximo documento toma el siguiente. */
export const correlativoFijarInput = z.object({ tipo: z.enum(TIPOS_ARRANQUE), ultimo: z.coerce.number().int().min(0).max(99_999_999) });
export type CorrelativoFijarInput = z.infer<typeof correlativoFijarInput>;

/** Rango del talonario de papel fiscal que se va a usar (viene impreso al pie de las hojas). */
export const rangoControlInput = z
  .object({ desde: z.coerce.number().int().min(1).max(99_999_999), hasta: z.coerce.number().int().min(1).max(99_999_999), nota: z.string().trim().max(200).optional() })
  .refine((r) => r.hasta >= r.desde, { message: 'El final del rango no puede ser menor que el inicio', path: ['hasta'] });
export type RangoControlInput = z.infer<typeof rangoControlInput>;

export interface CorrelativosSucursalDTO {
  sucursal_id: string;
  sucursal_nombre: string;
  serie: string;
  serie_manual: string | null;
  correlativos: { tipo: TipoArranque; etiqueta: string; ultimo: number; proximo: string; usado_hasta: number }[];
  rango_control: { id: string; desde: string; hasta: string; restantes: number; nota: string | null; creado_en: string } | null;
  rangos_anteriores: { id: string; desde: string; hasta: string; nota: string | null; creado_en: string }[];
  controles_anulados: number;
  /** Al cargar un talonario nuevo que no sigue al último control usado: los números que se saltan (decisión del 2026-10-27). */
  salto_talonario?: { desde: string; hasta: string; cantidad: number } | null;
}

// ─── Datos de la empresa (bloque 13, 2026-10-10) ────────────────────────────
/**
 * Identidad y datos fiscales de la empresa: salen en la cabecera de notas, cotizaciones, despachos y comprobantes de
 * retención, y en las declaraciones. Edición parcial: lo que no se envía no cambia.
 */
export const empresaEditarInput = z.strictObject({
  razon_social: z.string().trim().min(2, 'Indica la razón social').max(160).optional(),
  rif: rif.optional(),
  direccion_fiscal: z.string().trim().min(5, 'Indica la dirección fiscal').max(300).optional(),
  telefono: z.string().trim().max(80).nullable().optional(),
  correo: z.email('Correo inválido').max(160).nullable().optional(),
});
export type EmpresaEditarInput = z.infer<typeof empresaEditarInput>;

export interface EmpresaDTO {
  razon_social: string;
  rif: string;
  direccion_fiscal: string;
  telefono: string | null;
  correo: string | null;
  contribuyente_especial: boolean;
  actualizado_en: string;
}
